import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../apiClient';
import { Profile } from '../../hooks/useAuth';
import { Plus, X, Pencil, ToggleLeft, ToggleRight, AlertCircle, CheckCircle2, ShoppingBag, Clock } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';

interface Service {
  id: string;
  name: string;
  description: string | null;
  duration: number;
  price: number;
  is_active: boolean;
  created_at: string;
}

const emptyForm = { name: '', description: '', duration: '', price: '' };

export const ServiceCatalog: React.FC<{ profile: Profile }> = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/provider/services');
      if (!data.success) throw new Error(data.message);
      setServices(data.services);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (svc: Service) => {
    setEditingId(svc.id);
    setForm({
      name: svc.name,
      description: svc.description ?? '',
      duration: String(svc.duration),
      price: String(svc.price),
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        duration: Number(form.duration),
        price: Number(form.price),
      };
      const data = editingId
        ? await apiFetch(`/provider/services/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await apiFetch('/provider/services', { method: 'POST', body: JSON.stringify(payload) });
      if (!data.success) throw new Error(data.message);
      setSuccess(editingId ? 'Service updated.' : 'Service created.');
      setTimeout(() => setSuccess(null), 3000);
      setIsModalOpen(false);
      fetchServices();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (svc: Service) => {
    try {
      const data = await apiFetch(`/provider/services/${svc.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !svc.is_active }),
      });
      if (!data.success) throw new Error(data.message);
      fetchServices();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteService = async (id: string) => {
    if (!window.confirm('Deactivate this service? It will no longer appear on your storefront.')) return;
    try {
      const data = await apiFetch(`/provider/services/${id}`, { method: 'DELETE' });
      if (!data.success) throw new Error(data.message);
      fetchServices();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const setField = (field: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  if (loading) return (
    <div className="p-12 flex items-center justify-center">
      <p className="label-caps text-ink-faint">Loading services...</p>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h2 className="display text-2xl md:text-3xl text-ink">Services</h2>
          <p className="label-caps text-ink-faint mt-2">
            {services.length} service{services.length !== 1 ? 's' : ''} on your storefront
          </p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          <Plus size={16} /> Add service
        </Button>
      </div>

      {error && (
        <div className="p-4 flex items-center gap-3 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded-sm border border-red-200 dark:border-red-900 text-xs font-semibold">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="p-4 flex items-center gap-3 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 rounded-sm border border-green-200 dark:border-green-900 text-xs font-semibold">
          <CheckCircle2 size={16} className="shrink-0" /> {success}
        </div>
      )}

      {services.length === 0 ? (
        <EmptyState
          icon={ShoppingBag}
          title="No services yet"
          hint="Add your first service to start accepting bookings"
          action={<Button variant="primary" onClick={openCreate}><Plus size={16} /> Add service</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {services.map(svc => (
            <Card key={svc.id} accent={svc.is_active} className={`p-6 space-y-4 ${svc.is_active ? '' : 'opacity-60'}`}>
              <div className="flex items-start justify-between gap-4">
                <h3 className="display text-lg text-ink leading-tight">{svc.name}</h3>
                {svc.is_active
                  ? <Badge status="completed">Active</Badge>
                  : <Badge>Inactive</Badge>}
              </div>
              {svc.description && (
                <p className="text-xs text-ink-muted leading-relaxed">{svc.description}</p>
              )}
              <div className="flex items-center justify-between pt-3 border-t border-line">
                <div>
                  <p className="display text-2xl text-ink">RM {Number(svc.price).toFixed(2)}</p>
                  <p className="label-caps text-ink-faint mt-1 flex items-center gap-1.5">
                    <Clock size={11} /> {svc.duration} min
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => toggleActive(svc)} title={svc.is_active ? 'Deactivate' : 'Activate'}
                    className="p-2 text-ink-faint hover:text-ink hover:bg-surface rounded-sm transition-colors">
                    {svc.is_active
                      ? <ToggleRight size={20} className="text-green-600 dark:text-green-400" />
                      : <ToggleLeft size={20} />}
                  </button>
                  <button onClick={() => openEdit(svc)} title="Edit"
                    className="p-2 text-ink-faint hover:text-accent hover:bg-surface rounded-sm transition-colors">
                    <Pencil size={18} />
                  </button>
                  <button onClick={() => deleteService(svc.id)} title="Remove"
                    className="p-2 text-ink-faint hover:text-red-500 hover:bg-surface rounded-sm transition-colors">
                    <X size={18} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-raised w-full max-w-md rounded-sm border border-line overflow-hidden">
            <div className="bg-inverse px-6 md:px-8 py-5 flex items-center justify-between">
              <h3 className="display text-lg text-ink-inverse">
                {editingId ? 'Edit service' : 'New service'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" aria-label="Close">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-5">
              <Input required label="Service name" type="text" value={form.name} onChange={setField('name')}
                placeholder="e.g. Haircut, Consultation" />
              <Textarea label="Description (optional)" value={form.description} onChange={setField('description')} rows={2}
                placeholder="Brief description for customers" />
              <div className="grid grid-cols-2 gap-4">
                <Input required label="Duration (min)" type="number" min="1" value={form.duration} onChange={setField('duration')}
                  placeholder="60" />
                <Input required label="Price (RM)" type="number" min="0" step="0.01" value={form.price} onChange={setField('price')}
                  placeholder="50.00" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="ghost" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" className="flex-1" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
